/**
 * `cofactor workflow status|events|event ticket` + node deactivate/activate —
 * CLI management of the workflow management-status axis and its audit timeline.
 *
 * The management status (`active | paused | blocked | under_construction |
 * deprecated`) is the manual lifecycle control, distinct from approval
 * (`--status` on `workflow patch`) and computed health. Every change is
 * recorded server-side as a workflow event with actor, reason, and optional
 * ticket references (internal `iss_…` issues or external tracker URLs).
 */

import { readFileSync } from 'node:fs';
import { WORKFLOW_MANAGEMENT_STATUSES } from '@chatsdet/types';
import { apiRequest } from './cli-helpers.js';
import { getArgValue, hasFlag } from './auth.js';

type WorkflowEventTicketDto = {
  id: string;
  issueId: string | null;
  externalUrl: string | null;
  externalProvider: string | null;
  externalIdentifier: string | null;
};

type WorkflowEventDto = {
  id: string;
  eventType: string;
  fromStatus: string | null;
  toStatus: string | null;
  nodeId: string | null;
  nodeLabel: string | null;
  actorId: string;
  actorKind: string;
  reason: string | null;
  createdAt: string;
  tickets: WorkflowEventTicketDto[];
};

/** Resolve a `--reason @file.md` (read file) or inline string value. */
function readContent(value: string): string {
  if (value.startsWith('@')) {
    return readFileSync(value.slice(1), 'utf8');
  }
  return value;
}

function getAllArgValues(argv: string[], flag: string): string[] {
  const values: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === flag && i + 1 < argv.length) {
      values.push(argv[i + 1]!);
      i++;
    }
  }
  return values;
}

/** Map a `--ticket` value (iss_… id, raw UUID, or URL) to the API ticket shape. */
function toTicketPayload(value: string): Record<string, string> {
  if (/^https?:\/\//i.test(value)) return { url: value };
  return { issueId: value };
}

function formatTicket(ticket: WorkflowEventTicketDto): string {
  if (ticket.issueId) return `issue:${ticket.issueId}`;
  const label = ticket.externalIdentifier ? `${ticket.externalIdentifier} ` : '';
  return `${label}${ticket.externalUrl ?? ''}`.trim();
}

function formatEventLine(event: WorkflowEventDto): string {
  const when = event.createdAt.replace('T', ' ').slice(0, 19);
  const actor = `${event.actorKind === 'user' ? '' : `${event.actorKind}:`}${event.actorId}`;
  let what: string;
  switch (event.eventType) {
    case 'status_changed':
      what = `status ${event.fromStatus ?? '?'} → ${event.toStatus ?? '?'}`;
      break;
    case 'node_deactivated':
      what = `node deactivated${event.nodeLabel ? ` "${event.nodeLabel}"` : ''} (${event.nodeId ?? 'removed'})`;
      break;
    case 'node_reactivated':
      what = `node reactivated${event.nodeLabel ? ` "${event.nodeLabel}"` : ''} (${event.nodeId ?? 'removed'})`;
      break;
    default:
      what = event.eventType;
  }
  const reason = event.reason ? ` — ${event.reason}` : '';
  const tickets =
    event.tickets.length > 0 ? `\n      tickets: ${event.tickets.map(formatTicket).join(', ')}` : '';
  return `  ${when}  ${what}  [${actor}]${reason}${tickets}\n      event: ${event.id}`;
}

/**
 * `workflow status <id>` — show the three axes.
 * `workflow status <id> --set <status> [--reason <text|@file>] [--ticket <iss|url>]...`
 */
export async function handleWorkflowStatus(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error(
      'Usage: cofactor workflow status <workflowId> [--set <status> --reason "..." --ticket <iss_…|url>] [--json]'
    );
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const setValue = getArgValue(argv, '--set');

  if (!setValue) {
    type DetailResult = {
      ok: boolean;
      error?: string;
      workflow?: {
        id: string;
        name: string;
        status: string;
        managementStatus: string;
        health: string;
        pausedAt: string | null;
        firstPassedAt: string | null;
      };
    };
    const result = await apiRequest<DetailResult>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}`
    );
    if (result.error || !result.workflow) {
      console.error(`Error: ${result.error ?? 'Workflow not found'}`);
      process.exit(1);
    }
    const wf = result.workflow;
    if (jsonOutput) {
      console.log(
        JSON.stringify(
          {
            ok: true,
            workflowId: wf.id,
            name: wf.name,
            managementStatus: wf.managementStatus,
            approvalStatus: wf.status,
            health: wf.health,
          },
          null,
          2
        )
      );
      return;
    }
    console.log(`${wf.name} (${wf.id})`);
    console.log(`  management status: ${wf.managementStatus}`);
    console.log(`  approval:          ${wf.status}`);
    console.log(`  health:            ${wf.health}`);
    if (wf.managementStatus !== 'active') {
      console.log(`  (excluded from suite runs — set back with --set active)`);
    }
    return;
  }

  if (!(WORKFLOW_MANAGEMENT_STATUSES as readonly string[]).includes(setValue)) {
    console.error(
      `Error: Invalid status "${setValue}". Valid: ${WORKFLOW_MANAGEMENT_STATUSES.join(', ')}`
    );
    process.exit(1);
  }

  const rawReason = getArgValue(argv, '--reason');
  const reason = rawReason !== undefined ? readContent(rawReason).trim() : undefined;
  const tickets = getAllArgValues(argv, '--ticket').map(toTicketPayload);

  type StatusResult = {
    ok: boolean;
    error?: string;
    code?: string;
    changed?: boolean;
    workflow?: { id: string; name: string; managementStatus: string };
    event?: WorkflowEventDto | null;
  };
  const result = await apiRequest<StatusResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/status`,
    {
      status: setValue,
      ...(reason ? { reason } : {}),
      ...(tickets.length > 0 ? { tickets } : {}),
    }
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error}${result.code ? ` (${result.code})` : ''}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  if (result.changed === false) {
    console.log(`Workflow ${workflowId} is already ${setValue} — no change recorded.`);
    return;
  }
  const suffix =
    setValue === 'active'
      ? ' — it runs in suites again once eligible.'
      : ' — excluded from suite runs.';
  console.log(`Workflow ${workflowId} set to ${setValue}${suffix}`);
  if (result.event?.id) {
    console.log(`  event: ${result.event.id}${tickets.length ? ` (${tickets.length} ticket${tickets.length === 1 ? '' : 's'} attached)` : ''}`);
  }
}

/** `workflow events <id> [--limit N] [--json]` — the audit timeline. */
export async function handleWorkflowEvents(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: cofactor workflow events <workflowId> [--limit <n>] [--json]');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const limit = getArgValue(argv, '--limit');
  const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';

  type EventsResult = { ok: boolean; error?: string; data?: WorkflowEventDto[] };
  const result = await apiRequest<EventsResult>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/events${query}`
  );

  if (result.error) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  const events = result.data ?? [];
  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, events }, null, 2));
    return;
  }
  if (events.length === 0) {
    console.log('No events recorded for this workflow.');
    return;
  }
  console.log(`${events.length} event${events.length === 1 ? '' : 's'} (newest first):`);
  for (const event of events) {
    console.log(formatEventLine(event));
  }
}

/**
 * `workflow event ticket <workflowId> <eventId> --add <iss_…|url> [--provider p] [--identifier k]`
 * `workflow event ticket <workflowId> <eventId> --remove <ticketId>`
 */
export async function handleWorkflowEventTicket(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const workflowId = positional[0];
  const eventId = positional[1];
  const addValue = getArgValue(argv, '--add');
  const removeValue = getArgValue(argv, '--remove');
  const jsonOutput = hasFlag(argv, '--json');

  if (!workflowId || !eventId || (!addValue && !removeValue) || (addValue && removeValue)) {
    console.error(
      'Usage: cofactor workflow event ticket <workflowId> <eventId> --add <iss_…|url> [--provider <p>] [--identifier <key>]\n' +
        '       cofactor workflow event ticket <workflowId> <eventId> --remove <ticketId>'
    );
    process.exit(1);
  }

  if (addValue) {
    const provider = getArgValue(argv, '--provider');
    const identifier = getArgValue(argv, '--identifier');
    type AttachResult = { ok: boolean; error?: string; data?: WorkflowEventTicketDto };
    const result = await apiRequest<AttachResult>(
      apiUrl,
      token,
      'POST',
      `/workflows/${workflowId}/events/${eventId}/tickets`,
      {
        ...toTicketPayload(addValue),
        ...(provider ? { provider } : {}),
        ...(identifier ? { identifier } : {}),
      }
    );
    if (result.error) {
      console.error(`Error: ${result.error}`);
      process.exit(1);
    }
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }
    console.log(`Ticket attached to event ${eventId} (ticket id: ${result.data?.id ?? '?'}).`);
    return;
  }

  type DetachResult = { ok: boolean; error?: string; removed?: boolean };
  const result = await apiRequest<DetachResult>(
    apiUrl,
    token,
    'DELETE',
    `/workflows/${workflowId}/events/${eventId}/tickets/${removeValue}`
  );
  if (result.error) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  console.log(result.removed ? 'Ticket removed.' : 'No matching ticket found (nothing removed).');
}

/**
 * `workflow node deactivate|activate <workflowId> <nodeId> [--reason <text|@file>]` —
 * flips the node through the audited endpoint (config patch + workflow event),
 * replacing the old `node patch --config '{"deactivated":true}'` sugar which
 * recorded no actor or reason.
 */
export async function handleNodeDeactivateToggle(
  argv: string[],
  apiUrl: string,
  token: string,
  deactivate: boolean
): Promise<void> {
  const action = deactivate ? 'deactivate' : 'activate';
  const positional = argv.filter((a) => !a.startsWith('-'));
  const workflowId = positional[0];
  const nodeId = positional[1];
  if (!workflowId || !nodeId) {
    console.error('Error: Missing workflow ID and/or node ID.');
    console.error(
      `Usage: cofactor workflow node ${action} <workflowId> <nodeId> [--reason "..."] [--json]`
    );
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const rawReason = getArgValue(argv, '--reason');
  const reason = rawReason !== undefined ? readContent(rawReason).trim() : undefined;

  type ToggleResult = {
    ok: boolean;
    error?: string;
    changed?: boolean;
    event?: WorkflowEventDto | null;
  };
  const result = await apiRequest<ToggleResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/nodes/${nodeId}/${action}`,
    reason ? { reason } : {}
  );

  if (result.error) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  if (result.changed === false) {
    console.log(`Node ${nodeId} is already ${deactivate ? 'deactivated' : 'active'} — no change.`);
    return;
  }
  console.log(
    deactivate
      ? `Node ${nodeId} deactivated — it will be skipped during runs.`
      : `Node ${nodeId} reactivated.`
  );
}
